import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'scopeshield_split_ratio';
const DEFAULT_WIDTH = 60;
const MIN_WIDTH = 20;
const MAX_WIDTH = 80;

export function useResizableSplitPane() {
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed = saved ? parseFloat(saved) : DEFAULT_WIDTH;
    if (!Number.isFinite(parsed)) return DEFAULT_WIDTH;
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
  });

  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.classList.add('resizing-active');
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.body.classList.remove('resizing-active');
  }, []);

  const resize = useCallback((e) => {
    if (isResizing && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      const clampedWidth = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, newWidth)
      );
      setLeftWidth(clampedWidth);
      localStorage.setItem(STORAGE_KEY, clampedWidth.toString());
    }
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('resizing-active');
    };
  }, []);

  return { leftWidth, setLeftWidth, containerRef, isResizing, startResizing };
}